import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box
} from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/hooks/useLanguage';

const LanguageSelector: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage, changeLanguage, languages } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (languageCode: string) => {
    changeLanguage(languageCode);
    handleClose();
  };

  const currentLang = languages.find(lang => lang.code === currentLanguage);

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(42, 244, 151, 0.1)',
          }
        }}
        title={t('menu.language')}
      >
        <LanguageIcon />
      </IconButton>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          // Elevate the modal (root) above the mobile menu overlay (z-index 10000)
          root: { sx: { zIndex: 11001 } },
          paper: {
            sx: {
              backgroundColor: 'rgba(20, 20, 25, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(42, 244, 151, 0.2)',
              borderRadius: '8px',
              minWidth: 200,
            }
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {languages.map((language) => (
          <MenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            selected={currentLanguage === language.code}
            sx={{
              color: currentLanguage === language.code ? '#2af497' : 'white',
              '&:hover': {
                backgroundColor: 'rgba(42, 244, 151, 0.1)',
              },
              '&.Mui-selected': {
                backgroundColor: 'rgba(42, 244, 151, 0.2)',
                '&:hover': {
                  backgroundColor: 'rgba(42, 244, 151, 0.3)',
                }
              }
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
              <Typography variant="h6">{language.flag}</Typography>
            </ListItemIcon>
            <ListItemText 
              primary={language.name}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: currentLanguage === language.code ? 600 : 400
              }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSelector;

